// The authentication wall. Login is REQUIRED: an unauthenticated visitor sees only this gate and
// cannot reach the app. Once a session is present, the full app is mounted (dynamic import) and the
// gate fades away.
//
// OFFLINE-FIRST: the gate waits on auth.ready, which resolves once the cached session has been
// restored from localStorage (no network). A returning signed-in user goes straight to the app
// with no network round-trip; only a first-time login needs the network. To avoid a jarring flash,
// the gate holds a neutral splash until that decision is known, then either reveals the login form
// or mounts the app behind the still-covering splash.
//
// ONCE THE APP IS RUNNING, ONLY THE IN-APP SIGN OUT BRINGS THE WALL BACK. A session lost any other
// way (refresh token revoked by a sign-out on another device, expired; see auth.sessionLost) must
// never tear down a running chart and anchor watch — offline the user could not even sign back in.
// The app shows its own notice with an in-place sign-in instead.
//
// AN APP THAT FAILS TO LOAD SAYS SO. The app is one dynamically imported module: if its fetch fails
// (offline, and this build's code is not on the device) or its evaluation throws, the gate shows
// the failure and a Reload. A reload is the only retry: the page's module map keeps a module that
// failed to fetch or evaluate failed for the life of the page. A reload online fetches the newest
// release (navigations are network-first, public/sw.js), and the app's own update flow then makes
// its service worker current (src/app.ts, the build-id check) — the gate needs no update logic.

import { el } from './dom';
import { auth } from '../auth/store';
import { buildLoginForm } from './account';
import { pendingFriendToken } from '../social/friendLink';

// The brandmark: a chart compass rose. Drawn rather than an emoji so it's crisp at any size and
// inherits the accent via currentColor (the 8-point star uses its own teal gradient). Static markup
// → innerHTML is safe. An elongated, lightened north point reads as the bearing the name implies.
export const COMPASS_SVG =
  '<svg viewBox="0 0 100 100" width="100%" height="100%" aria-hidden="true">' +
  '<defs><linearGradient id="sbRose" x1="0" y1="0" x2="0" y2="1">' +
  '<stop offset="0" stop-color="#43d8c8"/><stop offset="1" stop-color="#1c7d75"/>' +
  '</linearGradient></defs>' +
  '<circle cx="50" cy="50" r="47" fill="none" stroke="currentColor" stroke-width="1.4" opacity="0.55"/>' +
  '<circle cx="50" cy="50" r="39" fill="none" stroke="currentColor" stroke-width="0.8" opacity="0.22"/>' +
  '<g stroke="currentColor" stroke-width="1.2" opacity="0.4">' +
  '<line x1="50" y1="3" x2="50" y2="7"/><line x1="73.5" y1="9.3" x2="71.5" y2="12.8"/>' +
  '<line x1="90.7" y1="26.5" x2="87.2" y2="28.5"/><line x1="97" y1="50" x2="93" y2="50"/>' +
  '<line x1="90.7" y1="73.5" x2="87.2" y2="71.5"/><line x1="73.5" y1="90.7" x2="71.5" y2="87.2"/>' +
  '<line x1="50" y1="97" x2="50" y2="93"/><line x1="26.5" y1="90.7" x2="28.5" y2="87.2"/>' +
  '<line x1="9.3" y1="73.5" x2="12.8" y2="71.5"/><line x1="3" y1="50" x2="7" y2="50"/>' +
  '<line x1="9.3" y1="26.5" x2="12.8" y2="28.5"/><line x1="26.5" y1="9.3" x2="28.5" y2="12.8"/>' +
  '</g>' +
  '<polygon points="50,8 53.1,42.6 67,33 57.4,46.9 92,50 57.4,53.1 67,67 53.1,57.4 50,92 46.9,57.4 33,67 42.6,53.1 8,50 42.6,46.9 33,33 46.9,42.6" fill="url(#sbRose)" opacity="0.95"/>' +
  '<polygon points="50,8 53.1,42.6 46.9,42.6" fill="#e9f2f8"/>' +
  '<circle cx="50" cy="50" r="3" fill="#07131c" stroke="currentColor" stroke-width="1"/>' +
  '</svg>';

export class AuthGate {
  readonly root: HTMLElement;
  private host: HTMLElement;
  /** The app behind the gate this page-load: not started, loading, on screen, or failed to load. */
  private app: 'none' | 'loading' | 'up' | 'failed' = 'none';
  private ready = false;

  /** @param mountApp mounts the full app (called once); resolves once it's on screen. */
  constructor(private mountApp: () => Promise<void>) {
    const rose = el('div', { class: 'sb-gate-rose' });
    rose.setAttribute('aria-hidden', 'true');
    rose.innerHTML = COMPASS_SVG;

    this.host = el('div', { class: 'sb-gate-host' }, [this.splash('Acquiring position…')]);
    this.root = el('div', { class: 'sb-gate' }, [
      el('div', { class: 'sb-gate-card' }, [
        el('div', { class: 'sb-gate-brand' }, [
          rose,
          el('div', { class: 'sb-gate-name' }, ['Seabearing']),
          el('div', { class: 'sb-gate-tag' }, ['Anchorage charts from real bathymetry.']),
        ]),
        this.host,
      ]),
    ]);

    // Decide only once the initial (cached) session is known — never flash the form first.
    void auth.ready.then(() => { this.ready = true; this.refresh(); });
    auth.subscribe(() => { if (this.ready) this.refresh(); });
  }

  private refresh(): void {
    const s = auth.snapshot;
    if (this.app === 'failed') return; // the failure stays on screen until the user reloads
    if (s.user) void this.toAuthed();
    else if (this.app !== 'none' && s.sessionLost) return; // involuntary loss: the app keeps running
    else this.toLogin();
  }

  private async toAuthed(): Promise<void> {
    if (this.app === 'up') { this.conceal(); return; }
    if (this.app === 'loading') return; // the splash covers until it is up
    this.app = 'loading';
    // Keep the gate covering with a splash while the app builds, so there's no flash of a
    // half-loaded map under a disappearing form.
    this.host.replaceChildren(this.splash('Plotting charts…'));
    this.root.classList.remove('sb-gate-login');
    try {
      await this.mountApp();
    } catch (err) {
      this.app = 'failed';
      console.error('app failed to load', err);
      this.host.replaceChildren(this.failure(err));
      return;
    }
    this.app = 'up';
    this.conceal();
  }

  private toLogin(): void {
    // The in-app Sign out AFTER the app was already running: reload for a clean reset back to the
    // wall rather than tearing down a live app in place.
    if (this.app !== 'none') { window.location.reload(); return; }
    // Opened a friend link while signed out: say what happens next, and that the link is not a way
    // in — Seabearing is invite-only, so a new account still needs an invite key.
    const friendNote = pendingFriendToken()
      ? el('div', { class: 'sb-gate-note' }, [
        el('strong', {}, ['You opened a friend link.']),
        ' Sign in and you’ll be asked to confirm it. Seabearing is invite-only: a friend link is not an invite key, so creating an account still needs one.',
      ])
      : null;
    this.host.replaceChildren(...(friendNote ? [friendNote] : []), buildLoginForm(''));
    this.root.classList.add('sb-gate-login');
    this.reveal();
  }

  private conceal(): void {
    this.root.classList.add('sb-gate-hidden');
  }
  private reveal(): void {
    this.root.classList.remove('sb-gate-hidden');
  }

  private splash(text: string): HTMLElement {
    return el('div', { class: 'sb-gate-splash' }, [el('span', { class: 'sb-spinner' }, [text])]);
  }

  /** The app did not load: why, and the one retry there is. */
  private failure(err: unknown): HTMLElement {
    const why = navigator.onLine
      ? 'Part of the app did not load. Reload to try again.'
      : 'No connection, and this version of the app is not stored on this device yet. Reload once you have signal.';
    const detail = err instanceof Error ? err.message : String(err);
    return el('div', {}, [
      el('div', { class: 'sb-gate-note', role: 'alert' }, [
        el('strong', {}, ['Seabearing could not start.']), ` ${why} (${detail})`,
      ]),
      el('button', { class: 'sb-btn primary sb-auth-primary', type: 'button', onclick: () => window.location.reload() }, ['Reload']),
    ]);
  }
}
